/**
 * Input Setup — touch controls, gamepad controls, charge state machines, DOM event listeners.
 *
 * Wires all input hardware (touch screen, gamepad, mouse, keyboard) into bonkjs
 * virtual key system so game logic sees unified key state.
 */

import { TouchControls, GamepadControls, GamepadButtons, Input, Keys } from 'bonkjs';
import type { Container } from 'pixi.js';
import { WORLD, ACTION, AIM } from '../constants';
import { ChargeStateMachine } from '../input-charge';
import type { Camera } from 'bonkjs';
import type { GameState } from '../types';

// ─── Constants ──────────────────────────────────────────────────────────────────

const GAMEPAD_BUTTONS_GAMEPLAY = [
  { button: GamepadButtons.A, key: ACTION.JUMP },
  { button: GamepadButtons.LT, key: ACTION.SPECIAL },
  { button: GamepadButtons.B, key: ACTION.CANCEL },
  { button: GamepadButtons.Y, key: ACTION.SWAP },
  { button: GamepadButtons.Start, key: Keys.Escape },
];
const GAMEPAD_BUTTONS_MENU = [
  { button: GamepadButtons.A, key: Keys.Space },
  { button: GamepadButtons.B, key: Keys.Escape },
  { button: GamepadButtons.Start, key: Keys.Space },
];
const GAMEPAD_LSTICK_MOVE = { keys: { left: Keys.ArrowLeft, right: Keys.ArrowRight } };
const GAMEPAD_LSTICK_FULL = { keys: { up: Keys.ArrowUp, down: Keys.ArrowDown, left: Keys.ArrowLeft, right: Keys.ArrowRight } };
const TOUCH_JOYSTICK_MOVE = { left: Keys.ArrowLeft, right: Keys.ArrowRight };
const TOUCH_JOYSTICK_FULL = { up: Keys.ArrowUp, down: Keys.ArrowDown, left: Keys.ArrowLeft, right: Keys.ArrowRight };

// Tank select split — gamepad 0 maps to WASD (P1 keyboard split), gamepad 1 maps to Arrows (P2 keyboard split)
const GAMEPAD_LSTICK_WASD = { keys: { up: Keys.W, down: Keys.S, left: Keys.A, right: Keys.D } };
const GAMEPAD_BUTTONS_TANKSEL_P1 = [
  { button: GamepadButtons.A, key: Keys.Space },
  { button: GamepadButtons.B, key: Keys.Escape },
  { button: GamepadButtons.Start, key: Keys.Space },
  { button: GamepadButtons.Y, key: Keys.Tab },      // terraformer mode toggle (P1 uses TAB)
];
const GAMEPAD_BUTTONS_TANKSEL_P2 = [
  { button: GamepadButtons.A, key: Keys.Enter },
  { button: GamepadButtons.B, key: Keys.Escape },
  { button: GamepadButtons.Start, key: Keys.Enter },
  { button: GamepadButtons.Y, key: Keys.ShiftLeft }, // terraformer mode toggle (P2 uses Shift)
];

// ─── Types ──────────────────────────────────────────────────────────────────────

export interface InputSetup {
  touchControls: TouchControls;
  gamepads: [GamepadControls, GamepadControls];
  chargeMouse: ChargeStateMachine;
  chargeKeyboard: ChargeStateMachine;
  chargeGamepads: [ChargeStateMachine, ChargeStateMachine];
  /** Mutable ref — tracks whether gamepad is in menu vs gameplay button mode. */
  gamepadInMenuMode: { value: boolean };
  /** Mutable ref — tracks whether gamepad left stick is in aim-split (L/R only) mode. */
  gamepadAimSplit: { value: boolean };
  /** Mutable ref — tracks whether touch joystick is in aim-split (L/R only) mode. */
  touchAimSplit: { value: boolean };
  /** Mutable ref — tracks whether mouse aim is active (persists until arrow keys override). */
  mouseAimActive: { value: boolean };
  /** Previous mouse position for move detection. */
  prevMouse: { x: number; y: number };
  /** Previous RT pressed state for edge detection (per gamepad). */
  prevRTPressed: [{ value: boolean }, { value: boolean }];
  /** Mutable ref — true when tank select has split gamepad sticks to WASD/Arrows. */
  gamepadTankSelectSplit: { value: boolean };
  /** Debug overlay toggle callback (DEV-only). */
  debugToggle: (() => void) | null;
  /** Set the debug toggle handler. */
  setDebugToggle: (fn: () => void) => void;
  /** Net debug overlay toggle callback (DEV-only). */
  netDebugToggle: (() => void) | null;
  /** Set the net debug toggle handler. */
  setNetDebugToggle: (fn: () => void) => void;
}

// ─── Setup ──────────────────────────────────────────────────────────────────────

export function setupInput(canvas: HTMLCanvasElement, uiContainer: Container): InputSetup {
  // Touch controls
  const touchControls = new TouchControls(uiContainer, {
    canvasWidth: WORLD.CANVAS_WIDTH,
    canvasHeight: WORLD.CANVAS_HEIGHT,
    joystick: {
      position: [0.12, 0.82],
      radius: 160,
      keys: {
        up: Keys.ArrowUp,
        down: Keys.ArrowDown,
        left: Keys.ArrowLeft,
        right: Keys.ArrowRight,
      },
    },
    buttons: [
      { key: ACTION.FIRE, holdKey: ACTION.CHARGE, cancelOnLeave: true, aimStick: true, aimStickDeadzone: 0.2, cancelRadius: 3.75, label: '◎', position: [0.90, 0.86], radius: 110, color: 0xff3333 },
      { key: ACTION.SPECIAL, label: '★', position: [0.79, 0.80], radius: 55, color: 0x4488ff },
      { key: ACTION.SWAP, label: '⇄', position: [0.85, 0.71], radius: 55, color: 0xffdd00 },
      { key: ACTION.JUMP, label: '▲', position: [0.79, 0.92], radius: 55, color: 0x44ff88 },
    ],
    autoHideOnKeyboard: true,
  });
  touchControls.hide();

  // Gamepad controls — two instances for local two-gamepad play
  const gamepad0 = new GamepadControls({
    leftStick: GAMEPAD_LSTICK_FULL,
    rightStick: false,
    buttons: GAMEPAD_BUTTONS_MENU,
    gamepadIndex: 0,
    autoSwitchMode: true,
  });
  gamepad0.show();
  const gamepad1 = new GamepadControls({
    leftStick: GAMEPAD_LSTICK_FULL,
    rightStick: false,
    buttons: GAMEPAD_BUTTONS_MENU,
    gamepadIndex: 1,
    autoSwitchMode: false, // only gamepad 0 triggers global input mode switch
  });
  gamepad1.show();

  // Charge state machines
  const chargeMouse = new ChargeStateMachine();
  const chargeGamepad0 = new ChargeStateMachine();
  const chargeGamepad1 = new ChargeStateMachine();
  const chargeKeyboard = new ChargeStateMachine();

  // Mutable refs for state tracking
  const gamepadInMenuMode = { value: true };
  const gamepadAimSplit = { value: false };
  const touchAimSplit = { value: false };
  const mouseAimActive = { value: false };
  const prevMouse = { x: 0, y: 0 };
  const prevRTPressed: [{ value: boolean }, { value: boolean }] = [{ value: false }, { value: false }];
  const gamepadTankSelectSplit = { value: false };
  let debugToggle: (() => void) | null = null;
  let netDebugToggle: (() => void) | null = null;

  // Mouse movement → keyboard mode
  canvas.addEventListener('pointermove', (e) => {
    if (e.pointerType === 'mouse') Input.setInputMode('keyboard');
  });

  // Prevent context menu on right click
  canvas.addEventListener('contextmenu', (e) => e.preventDefault());

  // Mouse charge/cancel
  canvas.addEventListener('mousedown', (e) => {
    if (Input.inputMode === 'touch') return;
    if (e.button === 0) chargeMouse.start();
    if (e.button === 2) chargeMouse.cancel();
  });
  window.addEventListener('mouseup', (e) => {
    if (Input.inputMode === 'touch') return;
    if (e.button === 0) chargeMouse.release();
  });

  // Keyboard Shift → Special virtual key
  window.addEventListener('keydown', (e) => {
    if (e.code === 'ShiftLeft' || e.code === 'ShiftRight')
      Input.setVirtualKey(ACTION.SPECIAL, true);
  });
  window.addEventListener('keyup', (e) => {
    if (e.code === 'ShiftLeft' || e.code === 'ShiftRight')
      Input.setVirtualKey(ACTION.SPECIAL, false);
  });

  // Keyboard Tab → swap weapon tier
  window.addEventListener('keydown', (e) => {
    if (e.code === 'Tab') { e.preventDefault(); Input.setVirtualKey(ACTION.SWAP, true); }
  });
  window.addEventListener('keyup', (e) => {
    if (e.code === 'Tab') Input.setVirtualKey(ACTION.SWAP, false);
  });

  // Keyboard Z → charge/fire, X → cancel charge
  window.addEventListener('keydown', (e) => {
    if (e.code === 'KeyZ' && !e.repeat) chargeKeyboard.start();
    if (e.code === 'KeyX') chargeKeyboard.cancel();
  });
  window.addEventListener('keyup', (e) => {
    if (e.code === 'KeyZ') chargeKeyboard.release();
  });

  // Ctrl+P: toggle physics debug overlay (DEV-only)
  // Ctrl+N: toggle net debug overlay (DEV-only)
  if (import.meta.env.DEV) {
    window.addEventListener('keydown', (e) => {
      if (e.code === 'KeyP' && e.ctrlKey) {
        e.preventDefault();
        debugToggle?.();
      }
      if (e.code === 'KeyN' && e.ctrlKey) {
        e.preventDefault();
        netDebugToggle?.();
      }
    });
  }

  return {
    touchControls,
    gamepads: [gamepad0, gamepad1] as [GamepadControls, GamepadControls],
    chargeMouse,
    chargeKeyboard,
    chargeGamepads: [chargeGamepad0, chargeGamepad1] as [ChargeStateMachine, ChargeStateMachine],
    gamepadInMenuMode,
    gamepadAimSplit,
    touchAimSplit,
    mouseAimActive,
    prevMouse,
    prevRTPressed,
    gamepadTankSelectSplit,
    debugToggle,
    setDebugToggle: (fn: () => void) => { debugToggle = fn; },
    netDebugToggle,
    setNetDebugToggle: (fn: () => void) => { netDebugToggle = fn; },
  };
}

// ─── Per-frame input update helpers ─────────────────────────────────────────────

/** Determine which player should have active gamepad input based on phase. */
export function getActiveInputPlayer(state: GameState): number {
  if (state.phase === 'di_window' && state.diActivePlayer >= 0) return state.diActivePlayer;
  if (state.phase === 'defensive_movement' && state.defenseActivePlayer >= 0) return state.defenseActivePlayer;
  if (state.phase === 'burst_window' && state.burstActivePlayer >= 0) return state.burstActivePlayer;
  return state.currentPlayer;
}

/** Update gamepad RT charge, button/stick mode switching, touch joystick mode. */
export function updateInputModes(
  input: InputSetup, state: GameState, canvas: HTMLCanvasElement,
): void {
  const { touchControls, gamepads, chargeMouse, chargeKeyboard, chargeGamepads,
    gamepadInMenuMode, gamepadAimSplit, touchAimSplit, prevRTPressed, gamepadTankSelectSplit } = input;

  const twoGamepads = gamepads[0].connected && gamepads[1].connected;

  const isMenu = state.phase === 'title_screen' || state.phase === 'mode_select' || state.phase === 'tank_select' || state.phase === 'stage_select' || state.phase === 'mp_lobby' || state.phase === 'mp_waiting';

  // Gamepad RT charge — poll from active player's gamepad when two connected
  const activePlayer = getActiveInputPlayer(state);
  const rtGpIndex = twoGamepads ? activePlayer : 0;
  const gp = navigator.getGamepads()[rtGpIndex];
  const rtPressed = gp?.buttons[GamepadButtons.RT]?.pressed ?? false;
  const rtAimActive = (state.phase === 'aiming' || state.phase === 'edgeguard') && !state.paused;
  if (rtAimActive) {
    if (rtPressed && !prevRTPressed[rtGpIndex].value) chargeGamepads[rtGpIndex].start();
    if (!rtPressed && prevRTPressed[rtGpIndex].value) chargeGamepads[rtGpIndex].release();
    if (Input.getKey(ACTION.CANCEL)) chargeGamepads[rtGpIndex].cancel();
  }
  prevRTPressed[0].value = rtAimActive && rtGpIndex === 0 ? rtPressed : false;
  prevRTPressed[1].value = rtAimActive && rtGpIndex === 1 ? rtPressed : false;

  // Touch controls: disabled on menus, enabled during gameplay
  if (isMenu && touchControls.isVisible) touchControls.hide();
  else if (!isMenu && !touchControls.isVisible) touchControls.show();

  // Tank select split: two gamepads → GP0 uses WASD+Space (P1), GP1 uses Arrows+Enter (P2)
  const needsTankSelectSplit = state.phase === 'tank_select' && twoGamepads && !state.multiplayer?.enabled;
  if (needsTankSelectSplit && !gamepadTankSelectSplit.value) {
    gamepadTankSelectSplit.value = true;
    gamepads[0].setLeftStick(GAMEPAD_LSTICK_WASD);
    gamepads[0].setButtons(GAMEPAD_BUTTONS_TANKSEL_P1);
    gamepads[1].setLeftStick(GAMEPAD_LSTICK_FULL);
    gamepads[1].setButtons(GAMEPAD_BUTTONS_TANKSEL_P2);
    gamepadInMenuMode.value = false; // override — we're managing buttons directly
  } else if (!needsTankSelectSplit && gamepadTankSelectSplit.value) {
    gamepadTankSelectSplit.value = false;
    // Restore normal mode — let the menu/gameplay switch below handle it
    gamepadInMenuMode.value = false;
  }

  // Gamepad button mode switching (skip when tank select split is active)
  if (!gamepadTankSelectSplit.value) {
    const needsMenuButtons = isMenu || state.paused;
    if (needsMenuButtons && !gamepadInMenuMode.value) {
      gamepadInMenuMode.value = true;
      for (const gp of gamepads) gp.setButtons(GAMEPAD_BUTTONS_MENU);
    } else if (!needsMenuButtons && gamepadInMenuMode.value) {
      gamepadInMenuMode.value = false;
      for (const gp of gamepads) gp.setButtons(GAMEPAD_BUTTONS_GAMEPLAY);
    }
  }

  // Aim split mode (skip when tank select split is active)
  const currentP = state.players[state.currentPlayer];
  const isLocalTurn = !(currentP?.isAI) && (!state.multiplayer?.enabled || state.currentPlayer === state.multiplayer.mySlot);
  const needsAimSplit = (state.phase === 'aiming' || state.phase === 'edgeguard') && !state.paused && isLocalTurn;
  if (!gamepadTankSelectSplit.value) {
    if (needsAimSplit && !gamepadAimSplit.value) {
      gamepadAimSplit.value = true;
      for (const gp of gamepads) gp.setLeftStick(GAMEPAD_LSTICK_MOVE);
    } else if (!needsAimSplit && gamepadAimSplit.value) {
      gamepadAimSplit.value = false;
      for (const gp of gamepads) gp.setLeftStick(GAMEPAD_LSTICK_FULL);
    }
  }
  if (needsAimSplit && !touchAimSplit.value) {
    touchAimSplit.value = true;
    touchControls.setJoystick(TOUCH_JOYSTICK_MOVE);
  } else if (!needsAimSplit && touchAimSplit.value) {
    touchAimSplit.value = false;
    touchControls.setJoystick(TOUCH_JOYSTICK_FULL);
  }

  // Reset charge state machines when leaving aiming
  if (!needsAimSplit) {
    chargeMouse.reset();
    chargeGamepads[0].reset();
    chargeGamepads[1].reset();
    chargeKeyboard.reset();
  }
  // Re-detect held triggers on aiming entry
  if (needsAimSplit) {
    chargeMouse.rearm();
    chargeKeyboard.rearm();
  }
}

/** Normalize atan2 raw angle [-π, π] to the full 360° aim range [AIM.MIN_ANGLE, AIM.MAX_ANGLE]. */
function clampDirectAimAngle(rawAngle: number): number {
  // atan2 returns [-π, π]. Shift angles below -π/2 into [π, 3π/2] to stay in range.
  return rawAngle >= AIM.MIN_ANGLE ? rawAngle : rawAngle + Math.PI * 2;
}

/** Update direct aim from gamepad right stick, mouse, and touch aim stick. */
export function updateDirectAim(
  input: InputSetup, state: GameState, canvas: HTMLCanvasElement, camera: Camera,
): void {
  const currentP = state.players[state.currentPlayer];
  const isLocalTurn = !(currentP?.isAI) && (!state.multiplayer?.enabled || state.currentPlayer === state.multiplayer.mySlot);
  if ((state.phase !== 'aiming' && state.phase !== 'edgeguard') || state.paused || !isLocalTurn) return;

  const player = state.players[state.currentPlayer];

  // Right stick direct aim (gamepad mode only, aim-only, lock during FIRE pulse)
  if (Input.inputMode === 'gamepad' && !Input.getKey(ACTION.FIRE)) {
    const twoGp = input.gamepads[0].connected && input.gamepads[1].connected;
    const rs = input.gamepads[twoGp ? state.currentPlayer : 0].rightStickValue;
    const rsMag = Math.sqrt(rs.x * rs.x + rs.y * rs.y);
    if (rsMag > 0.1) {
      player.angle = clampDirectAimAngle(Math.atan2(-rs.y, rs.x));
      input.mouseAimActive.value = false;
    }
  }

  // Mouse direct aim (keyboard mode only)
  if (Input.inputMode === 'keyboard') {
    const [mx, my] = Input.mousePosition;
    if (mx !== input.prevMouse.x || my !== input.prevMouse.y) {
      input.prevMouse.x = mx;
      input.prevMouse.y = my;
      input.mouseAimActive.value = true;
    }
    if (Input.getKey(Keys.ArrowUp) || Input.getKey(Keys.ArrowDown)) input.mouseAimActive.value = false;
    if (input.mouseAimActive.value) {
      const rect = canvas.getBoundingClientRect();
      const canvasX = input.prevMouse.x * (canvas.width / rect.width);
      const canvasY = input.prevMouse.y * (canvas.height / rect.height);
      const [wx, wy] = camera.screenToWorld(canvasX, canvasY);
      player.angle = clampDirectAimAngle(Math.atan2(-(wy - player.y), wx - player.x));
    }
  }

  // Touch direct aim (touch mode only)
  if (Input.inputMode === 'touch') {
    const ts = input.touchControls.aimStickValue;
    const tsMag = Math.sqrt(ts.x * ts.x + ts.y * ts.y);
    if (tsMag > 0.1) {
      player.angle = clampDirectAimAngle(Math.atan2(ts.y, -ts.x));
      player.slingshotCenter = 0.1 + tsMag * 0.9; // map 0..1 → 0.1..1.0
      input.mouseAimActive.value = false;
    }
    // When in deadzone (tsMag <= 0.1), keep previous slingshotCenter
  }
}
